import React, { useState } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { Calendar, MapPin, CheckCircle2, ShieldCheck, ArrowRight, Wrench, Clock, User, Phone } from 'lucide-react';

export const SiteAuditScheduler: React.FC = () => {
  const { selectedPreset } = useSolarStore();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [state, setState] = useState('Lagos');
  const [address, setAddress] = useState('');
  const [auditDate, setAuditDate] = useState('');
  const [auditTime, setAuditTime] = useState('Morning (9AM - 12PM)');
  const [isScheduled, setIsScheduled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !phone || !address || !auditDate) return;

    setIsSubmitting(true);
    try {
      await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName,
          whatsappNumber: phone,
          state,
          selectedPresetId: selectedPreset.id,
          dailyFuelSpend: 15000,
          consultationType: `Physical Site Audit Scheduled for ${auditDate} (${auditTime}) at ${address}`,
        }),
      });
      setIsScheduled(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl text-white space-y-8 my-10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2">
            <Wrench className="w-3.5 h-3.5" /> Professional Engineering Dispatch • Umuahia HQ
          </div>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
            Schedule a Physical On-Site Solar Audit
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Dispatch COREN certified engineers directly from our headquarters at <strong className="text-white">2 Abiriba St, Uzuokoli Road, Umuahia, 440233, Abia State</strong> or call <strong className="text-amber-400">0806 475 2074</strong>.
          </p>
        </div>

        <div className="bg-slate-950 px-5 py-3 rounded-2xl border border-emerald-500/35 text-right space-y-1">
          <span className="text-xs text-slate-400 block font-medium">Headquarters Helpline</span>
          <a href="tel:+2348064752074" className="text-lg font-black text-amber-400 block hover:underline">0806 475 2074</a>
          <span className="text-[10px] text-emerald-400 font-bold block">Free Site Survey Promo</span>
        </div>
      </div>

      {isScheduled ? (
        <div className="bg-emerald-950/40 border border-emerald-500/50 p-8 rounded-2xl text-center space-y-4">
          <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h4 className="text-2xl font-black text-white">Site Audit Successfully Scheduled!</h4>
          <p className="text-sm text-slate-300 max-w-md mx-auto">
            Thank you, <strong className="text-white">{fullName}</strong>. Our Lagos/Umuahia/Abuja engineering team has received your dispatch request for <strong className="text-emerald-400">{auditDate}</strong>. We will call you shortly to confirm.
          </p>
          <button
            onClick={() => setIsScheduled(false)}
            className="bg-slate-800 hover:bg-slate-700 text-white px-6 py-2.5 rounded-xl text-xs font-bold transition-all"
          >
            Book Another Inspection
          </button>
        </div>
      ) : (
        <form onSubmit={handleSchedule} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6 space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-400" /> Full Name / Contact Person
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Engr. Samuel Okoro"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                <Phone className="w-4 h-4 text-emerald-400" /> WhatsApp / Phone Number
              </label>
              <input
                type="tel"
                required
                placeholder="e.g. 08064752074"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-400" /> State
                </label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500"
                >
                  <option value="Lagos">Lagos State</option>
                  <option value="Abuja">Abuja (FCT)</option>
                  <option value="Rivers">Rivers (Port Harcourt)</option>
                  <option value="Abia">Abia (Umuahia / Aba)</option>
                  <option value="Oyo">Oyo (Ibadan)</option>
                  <option value="Other">Other State</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-400" /> Preferred Slot
                </label>
                <select
                  value={auditTime}
                  onChange={(e) => setAuditTime(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500"
                >
                  <option value="Morning (9AM - 12PM)">Morning (9AM - 12PM)</option>
                  <option value="Afternoon (1PM - 4PM)">Afternoon (1PM - 4PM)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-400" /> Full Property Street Address
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="e.g. Plot 12, Admiralty Way, Lekki Phase 1, Lagos"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500 resize-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-emerald-400" /> Preferred Inspection Date
                </label>
                <input
                  type="date"
                  required
                  value={auditDate}
                  onChange={(e) => setAuditDate(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-4 rounded-xl shadow-lg shadow-emerald-500/25 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            >
              {isSubmitting ? 'Dispatching Engineer...' : 'Dispatch COREN Engineer for Site Audit'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
